/**
 * NOS-124: Create / edit media video (YouTube, audio, taxonomies).
 */
import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ExternalLink, Loader2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { MediaSectionNav } from "@/components/dashboard/MediaSectionNav"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { useOrgStore } from "@/stores/orgStore"
import { useAuthStore } from "@/stores/authStore"
import {
  useMediaVideo,
  useCreateMediaVideo,
  useUpdateMediaVideo,
  maybeInvokeTranscribeAfterSave,
  type MediaVideoWithRelations,
  type SaveMediaVideoInput,
} from "@/hooks/useMediaVideos"
import { useMediaSeries } from "@/hooks/useMediaSeries"
import { useMediaSpeakers } from "@/hooks/useMediaSpeakers"
import { useMediaTopics } from "@/hooks/useMediaTopics"
import { useCreateBlogPost } from "@/hooks/useBlogPosts"
import { generateSlug } from "@/lib/slugUtils"
import { extractYouTubeVideoId, fetchYouTubeOEmbed } from "@/lib/youtubeUtils"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"
import type { Json } from "@/types/database"

function videoToSpeakerIds(v: MediaVideoWithRelations): string[] {
  return (v.media_video_speakers ?? []).map((r) => r.speaker_id)
}

function videoToTopicIds(v: MediaVideoWithRelations): string[] {
  return (v.media_video_topics ?? []).map((r) => r.topic_id)
}

export function MediaEditorPage() {
  const { videoId } = useParams<{ videoId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const isNew = !videoId || videoId === "new"

  const { video, loading: videoLoading, refresh } = useMediaVideo(orgId, isNew ? null : videoId ?? null)
  const { createMediaVideo, loading: creating } = useCreateMediaVideo(orgId)
  const { updateMediaVideo, loading: updating } = useUpdateMediaVideo(orgId)
  const { createBlogPost, loading: blogLoading } = useCreateBlogPost(orgId)

  const { rows: series, refresh: refreshSeries, create: createSeries } = useMediaSeries(orgId)
  const { rows: speakers } = useMediaSpeakers(orgId)
  const { rows: topics, refresh: refreshTopics, create: createTopic } = useMediaTopics(orgId)

  const [youtubeUrl, setYoutubeUrl] = useState("")
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [description, setDescription] = useState("")
  const [thumbnailUrl, setThumbnailUrl] = useState("")
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [audioFileName, setAudioFileName] = useState<string | null>(null)
  const [seriesId, setSeriesId] = useState<string | null>(null)
  const [speakerIds, setSpeakerIds] = useState<string[]>([])
  const [topicIds, setTopicIds] = useState<string[]>([])
  const [scripturePassage, setScripturePassage] = useState("")
  const [isPublished, setIsPublished] = useState(false)
  const [publishedAt, setPublishedAt] = useState("")
  const [newSeriesOpen, setNewSeriesOpen] = useState(false)
  const [newSeriesTitle, setNewSeriesTitle] = useState("")
  const [newTopicName, setNewTopicName] = useState("")
  const [oembedLoading, setOembedLoading] = useState(false)
  const [audioUploading, setAudioUploading] = useState(false)
  const [slugManual, setSlugManual] = useState(false)

  useEffect(() => {
    if (video) {
      setSlugManual(false)
      setYoutubeUrl(video.youtube_url ?? "")
      setTitle(video.title)
      setSlug(video.slug)
      setDescription(video.description ?? "")
      setThumbnailUrl(video.thumbnail_url ?? "")
      setYoutubeVideoId(video.youtube_video_id)
      setAudioUrl(video.audio_url)
      setAudioFileName(video.audio_url ? video.audio_url.split("/").pop() ?? "audio.mp3" : null)
      setSeriesId(video.series_id)
      setSpeakerIds(videoToSpeakerIds(video))
      setTopicIds(videoToTopicIds(video))
      setScripturePassage(video.scripture_passage ?? "")
      setIsPublished(video.is_published)
      setPublishedAt(video.published_at ? video.published_at.slice(0, 16) : "")
    } else if (isNew) {
      setSlugManual(false)
      setYoutubeUrl("")
      setTitle("")
      setSlug("")
      setDescription("")
      setThumbnailUrl("")
      setYoutubeVideoId(null)
      setAudioUrl(null)
      setAudioFileName(null)
      setSeriesId(null)
      setSpeakerIds([])
      setTopicIds([])
      setScripturePassage("")
      setIsPublished(false)
      setPublishedAt("")
    }
  }, [video, isNew])

  const applyYoutubeUrl = useCallback(async (url: string) => {
    const id = extractYouTubeVideoId(url)
    setYoutubeVideoId(id)
    if (!id) return
    setOembedLoading(true)
    const meta = await fetchYouTubeOEmbed(url)
    setOembedLoading(false)
    if (meta.title) {
      setTitle((t) => (t.trim() === "" ? meta.title! : t))
      setSlug((s) => (slugManual ? s : generateSlug(meta.title!)))
    }
    if (meta.thumbnail_url) {
      setThumbnailUrl((th) => (th.trim() === "" ? meta.thumbnail_url! : th))
    }
  }, [slugManual])

  const buildPayload = (): SaveMediaVideoInput => ({
    title,
    slug: slug.trim() || generateSlug(title),
    description,
    youtube_url: youtubeUrl.trim() || null,
    youtube_video_id: youtubeVideoId,
    thumbnail_url: thumbnailUrl.trim() || null,
    audio_url: audioUrl,
    series_id: seriesId,
    speaker_ids: speakerIds,
    topic_ids: topicIds,
    scripture_passage: scripturePassage.trim() || null,
    is_published: isPublished,
    published_at: publishedAt ? new Date(publishedAt).toISOString() : null,
  })

  const handleSave = async () => {
    if (!orgId) return
    if (!title.trim()) {
      toast.error("Title is required")
      return
    }
    const payload = buildPayload()
    if (isNew) {
      const row = await createMediaVideo(payload)
      if (!row) {
        toast.error("Could not create video")
        return
      }
      toast.success("Video created")
      maybeInvokeTranscribeAfterSave(row.id, orgId, row.youtube_video_id, row.transcript_status)
      navigate(`/dashboard/media/${row.id}/edit`, { replace: true })
      return
    }
    const ok = await updateMediaVideo(videoId!, payload)
    if (!ok) {
      toast.error("Could not update video")
      return
    }
    toast.success("Saved")
    await refresh()
    const { data: row } = await supabase
      .from("media_videos")
      .select("transcript_status, youtube_video_id")
      .eq("id", videoId!)
      .eq("org_id", orgId)
      .maybeSingle()
    maybeInvokeTranscribeAfterSave(
      videoId!,
      orgId,
      row?.youtube_video_id ?? youtubeVideoId,
      row?.transcript_status
    )
  }

  const handlePublishToBlog = async () => {
    if (!orgId || !video?.ai_blog_draft) return
    const draft = video.ai_blog_draft
    if (draft === null || (Array.isArray(draft) && draft.length === 0)) {
      toast.error("No AI draft available yet")
      return
    }
    const created = await createBlogPost({
      title: `${video.title} (sermon)`,
      slug: generateSlug(`${video.title}-sermon-${Date.now()}`),
      content_sections: draft as Json,
      is_published: false,
      author_id: user?.id ?? null,
      cover_image: video.thumbnail_url,
    })
    if (!created) {
      toast.error("Could not create blog draft")
      return
    }
    toast.success("Draft blog post created")
    navigate(`/dashboard/blog/${created.id}/edit`)
  }

  const handleAudioFile = async (file: File | null) => {
    if (!file || !orgId) return
    if (file.type !== "audio/mpeg") {
      toast.error("Please upload an MP3 file")
      return
    }
    setAudioUploading(true)
    const path = `${orgId}/media/audio/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`
    const { error } = await supabase.storage.from("media").upload(path, file, {
      contentType: file.type,
      upsert: false,
    })
    if (error) {
      toast.error(error.message)
      setAudioUploading(false)
      return
    }
    const { data: pub } = supabase.storage.from("media").getPublicUrl(path)
    setAudioUrl(pub.publicUrl)
    setAudioFileName(file.name)
    setAudioUploading(false)
    toast.success("Audio uploaded")
  }

  const removeAudio = () => {
    setAudioUrl(null)
    setAudioFileName(null)
  }

  const handleNewSeries = async () => {
    const r = await createSeries({ title: newSeriesTitle })
    if (r.ok && r.row) {
      setSeriesId(r.row.id)
      setNewSeriesOpen(false)
      setNewSeriesTitle("")
      await refreshSeries()
      toast.success("Series created")
    } else {
      toast.error(r.error ?? "Could not create series")
    }
  }

  const addTopicInline = async () => {
    if (!newTopicName.trim()) return
    const r = await createTopic({ name: newTopicName })
    if (r.ok && r.row) {
      const topicId = r.row.id
      setTopicIds((ids) => [...ids, topicId])
      setNewTopicName("")
      await refreshTopics()
      toast.success("Topic added")
    } else {
      toast.error(r.error ?? "Could not create topic")
    }
  }

  const toggleSpeaker = (id: string, on: boolean) => {
    setSpeakerIds((ids) => (on ? [...ids, id] : ids.filter((x) => x !== id)))
  }

  const toggleTopic = (id: string, on: boolean) => {
    setTopicIds((ids) => (on ? [...ids, id] : ids.filter((x) => x !== id)))
  }

  const saving = creating || updating
  const loading = videoLoading && !isNew

  const hasAiDraft =
    video?.ai_blog_draft !== null &&
    video?.ai_blog_draft !== undefined &&
    JSON.stringify(video.ai_blog_draft) !== "[]" &&
    (Array.isArray(video.ai_blog_draft) ? video.ai_blog_draft.length > 0 : true)

  return (
    <PermissionGate permission="can_manage_media" fallback={<Navigate to="/dashboard" replace />}>
      <div className="min-w-0 mx-auto max-w-3xl space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
              <Link to="/dashboard/media">← Media library</Link>
            </Button>
            <MediaSectionNav />
            <h1 className="text-2xl font-bold">{isNew ? "New video" : "Edit video"}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isNew && video?.slug && currentOrg?.slug && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`/${currentOrg.slug}/media/${video.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  View on site
                </a>
              </Button>
            )}
            {!isNew && hasAiDraft && (
              <Button variant="secondary" size="sm" onClick={handlePublishToBlog} disabled={blogLoading}>
                {blogLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish to Blog"}
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="yt">YouTube URL</Label>
              <Input
                id="yt"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                onBlur={() => youtubeUrl.trim() && applyYoutubeUrl(youtubeUrl)}
                onPaste={(e) => {
                  const t = e.clipboardData.getData("text")
                  setTimeout(() => t && applyYoutubeUrl(t), 0)
                }}
                placeholder="https://www.youtube.com/watch?v=…"
              />
              {oembedLoading && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Fetching video info…
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => {
                  const v = e.target.value
                  setTitle(v)
                  if (!slugManual) {
                    setSlug(generateSlug(v))
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">URL slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => {
                  setSlugManual(true)
                  setSlug(e.target.value)
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="desc">Description</Label>
              <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
            </div>

            <div className="space-y-2">
              <Label>Audio (optional MP3)</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="file"
                  accept="audio/mpeg"
                  className="max-w-xs"
                  disabled={audioUploading}
                  onChange={(e) => handleAudioFile(e.target.files?.[0] ?? null)}
                />
                {audioUploading && <Loader2 className="h-4 w-4 animate-spin" />}
                {audioFileName && (
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    {audioFileName}
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={removeAudio}>
                      <X className="h-4 w-4" />
                    </Button>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Upload an MP3 for podcast / audio playback.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Series</Label>
                <div className="flex gap-2">
                  <Select value={seriesId ?? "__none__"} onValueChange={(v) => setSeriesId(v === "__none__" ? null : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select series" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {series.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={() => setNewSeriesOpen(true)}>
                    New…
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pub-at">Publish at</Label>
                <Input
                  id="pub-at"
                  type="datetime-local"
                  value={publishedAt}
                  onChange={(e) => setPublishedAt(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Speakers</Label>
              <div className="h-36 overflow-y-auto rounded-md border p-3">
                <div className="space-y-2">
                  {speakers.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={speakerIds.includes(s.id)}
                        onCheckedChange={(c) => toggleSpeaker(s.id, c === true)}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Topics</Label>
              <div className="h-36 overflow-y-auto rounded-md border p-3">
                <div className="space-y-2">
                  {topics.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={topicIds.includes(t.id)}
                        onCheckedChange={(c) => toggleTopic(t.id, c === true)}
                      />
                      {t.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 max-w-md">
                <Input
                  placeholder="New topic name"
                  value={newTopicName}
                  onChange={(e) => setNewTopicName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTopicInline())}
                />
                <Button type="button" variant="outline" onClick={addTopicInline}>
                  Add topic
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="scripture">Scripture passage</Label>
              <Input
                id="scripture"
                value={scripturePassage}
                onChange={(e) => setScripturePassage(e.target.value)}
                placeholder="e.g. Romans 8:1–11"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch id="pub" checked={isPublished} onCheckedChange={setIsPublished} />
              <Label htmlFor="pub">Published</Label>
            </div>
          </div>
        )}

        <Dialog open={newSeriesOpen} onOpenChange={setNewSeriesOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New series</DialogTitle>
            </DialogHeader>
            <Input
              value={newSeriesTitle}
              onChange={(e) => setNewSeriesTitle(e.target.value)}
              placeholder="Series title"
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleNewSeries())}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewSeriesOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleNewSeries}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PermissionGate>
  )
}
